import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import ar from '@/locales/ar.json';
import en from '@/locales/en.json';

// Helper function to get system language
export const getSystemLanguage = (): 'en' | 'ar' => {
  const systemLang = navigator.language || navigator.languages?.[0] || 'en';
  const langCode = systemLang.toLowerCase().split('-')[0];

  // Check if system language is supported
  if (langCode === 'ar') {
    return 'ar';
  }

  // Default to English for all other languages
  return 'en';
};

// Helper function to get initial language with priority: localStorage > system > 'en'
const getInitialLanguage = (): 'en' | 'ar' => {
  // First, check if user has previously selected a language
  const savedLanguage = localStorage.getItem('i18nextLng') as 'en' | 'ar' | null;
  if (savedLanguage && ['en', 'ar'].includes(savedLanguage)) {
    return savedLanguage;
  }

  // If no saved preference, use system language
  return getSystemLanguage();
};

// Utility function to get language source for debugging
export const getLanguageSource = (): 'localStorage' | 'system' | 'fallback' => {
  const savedLanguage = localStorage.getItem('i18nextLng') as 'en' | 'ar' | null;
  if (savedLanguage && ['en', 'ar'].includes(savedLanguage)) {
    return 'localStorage';
  }

  const systemLang = getSystemLanguage();
  if (systemLang !== 'en') {
    return 'system';
  }

  return 'fallback';
};

export const initializeI18n = (): Promise<void> => {
  const initialLanguage = getInitialLanguage();

  return i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        en: { translation: en },
        ar: { translation: ar }
      },
      lng: initialLanguage,
      fallbackLng: 'en',
      supportedLngs: ['en', 'ar'],
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        // Override the default detection to use our custom logic
        lookupLocalStorage: 'i18nextLng',
        lookupSessionStorage: false,
        lookupQuerystring: false,
        lookupCookie: false,
        lookupFromPathIndex: 0,
        lookupFromSubdomainIndex: 0,
        caches: ['localStorage'],
        excludeCacheFor: ['cimode'],
        cookieExpirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
        cookieSecure: false,
        cookieDomain: undefined,
        cookiePath: '/',
        cookieSameSite: 'strict'
      },
      interpolation: {
        escapeValue: false
      }
    })
    .then(() => {
      // Initialize document direction based on initial language
      document.documentElement.dir = initialLanguage === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = initialLanguage;
    });
};

// Language switching utility
export const changeLanguage = (language: 'en' | 'ar') => {
  i18n.changeLanguage(language);
  // Save to localStorage for persistence
  localStorage.setItem('i18nextLng', language);
  // Update document direction for RTL support
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = language;
};

// Get current language
export const getCurrentLanguage = (): 'en' | 'ar' => {
  return i18n.language as 'en' | 'ar';
};

// Check if current language is RTL
export const isRTL = (): boolean => {
  return getCurrentLanguage() === 'ar';
};

/** App default when no server preference exists (system language, else en). */
export const getAppDefaultLanguage = (): 'en' | 'ar' => {
  return getSystemLanguage();
};
